/**
 * Procedural Ambient Sound Generators
 * 
 * Creates atmospheric background sounds for the war room / command center feel.
 * Designed for long play sessions - subtle, non-intrusive, loop-safe.
 * 
 * All sounds are procedurally generated - no audio files required.
 */

import { AMBIENT_PARAMS } from '../audioConfig.js';

// =====================================================================
// WAR ROOM AMBIENT - Atmospheric pad, subtle electronics, gentle movement
// =====================================================================

/**
 * Create war room ambient sound
 * Returns an object with stop() method to control playback
 */
export function createWarRoomAmbient(ctx, destination) {
  if (!ctx || !destination) return null;

  const params = AMBIENT_PARAMS.warRoom;
  const now = ctx.currentTime;

  // Track all nodes for cleanup
  const nodes = [];
  let isPlaying = true;
  let blipTimeoutId = null;

  // Master gain for fade in/out
  const masterGain = ctx.createGain();
  masterGain.gain.setValueAtTime(0, now);
  masterGain.gain.linearRampToValueAtTime(0.8, now + params.fadeInDuration / 1000);
  masterGain.connect(destination);
  nodes.push(masterGain);

  // ---------------------------------------------------------------
  // Layer 1: Warm pad chord (C minor - C, Eb, G)
  // Much more pleasant than a single bass drone
  // ---------------------------------------------------------------
  const padNotes = [130.81, 155.56, 196.00]; // C3, Eb3, G3
  
  padNotes.forEach((freq, i) => {
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq, now);
    
    // Gentle vibrato
    const vibrato = ctx.createOscillator();
    vibrato.type = 'sine';
    vibrato.frequency.setValueAtTime(0.3 + i * 0.1, now);
    
    const vibratoGain = ctx.createGain();
    vibratoGain.gain.setValueAtTime(2, now);
    
    vibrato.connect(vibratoGain);
    vibratoGain.connect(osc.frequency);
    
    // Low pass for warmth
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(800, now);
    filter.Q.value = 0.5;
    
    const oscGain = ctx.createGain();
    oscGain.gain.setValueAtTime(0.04, now);
    
    osc.connect(filter);
    filter.connect(oscGain);
    oscGain.connect(masterGain);
    
    osc.start(now);
    vibrato.start(now);
    nodes.push(osc, vibrato, vibratoGain, filter, oscGain);
  });

  // ---------------------------------------------------------------
  // Layer 2: Gentle filtered noise (air conditioning / room tone)
  // ---------------------------------------------------------------
  const noiseNode = createPinkNoise(ctx);
  const noiseGain = ctx.createGain();
  noiseGain.gain.setValueAtTime(0.015, now);

  const noiseFilter = ctx.createBiquadFilter();
  noiseFilter.type = 'bandpass';
  noiseFilter.frequency.setValueAtTime(400, now);
  noiseFilter.Q.value = 0.3;

  noiseNode.connect(noiseFilter);
  noiseFilter.connect(noiseGain);
  noiseGain.connect(masterGain);

  noiseNode.start(now);
  nodes.push(noiseNode, noiseFilter, noiseGain);

  // ---------------------------------------------------------------
  // Layer 3: Occasional soft blips (radar/electronics)
  // ---------------------------------------------------------------
  function scheduleBlip() {
    if (!isPlaying) return;

    const delay = randomBetween(4000, 10000);
    
    blipTimeoutId = setTimeout(() => {
      if (!isPlaying) return;
      
      playSoftBlip(ctx, masterGain);
      scheduleBlip();
    }, delay);
  }

  scheduleBlip();

  // ---------------------------------------------------------------
  // Return control object
  // ---------------------------------------------------------------
  return {
    stop: () => {
      isPlaying = false;
      
      if (blipTimeoutId) {
        clearTimeout(blipTimeoutId);
      }

      // Fade out
      const fadeStart = ctx.currentTime;
      masterGain.gain.setValueAtTime(masterGain.gain.value, fadeStart);
      masterGain.gain.linearRampToValueAtTime(0, fadeStart + params.fadeOutDuration / 1000);

      // Stop and disconnect after fade
      setTimeout(() => {
        nodes.forEach(node => {
          try {
            if (node.stop) node.stop();
            node.disconnect();
          } catch (e) {
            // Node may already be stopped
          }
        });
      }, params.fadeOutDuration + 100);
    }
  };
}

/**
 * Play a soft radar-like blip
 */
function playSoftBlip(ctx, destination) {
  const now = ctx.currentTime;
  const freq = randomBetween(600, 1200);

  const osc = ctx.createOscillator();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(freq, now);
  osc.frequency.exponentialRampToValueAtTime(freq * 0.8, now + 0.15);

  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0, now);
  gain.gain.linearRampToValueAtTime(0.02, now + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.001, now + 0.2);

  osc.connect(gain);
  gain.connect(destination);

  osc.start(now);
  osc.stop(now + 0.25);

  osc.onended = () => {
    osc.disconnect();
    gain.disconnect();
  };
}

// =====================================================================
// TENSION AMBIENT - Darker, more dramatic atmosphere
// =====================================================================

/**
 * Create tension ambient sound
 * More ominous than war room, for combat situations
 */
export function createTensionAmbient(ctx, destination) {
  if (!ctx || !destination) return null;

  const params = AMBIENT_PARAMS.tension;
  const now = ctx.currentTime;

  const nodes = [];
  let isPlaying = true;

  // Master gain
  const masterGain = ctx.createGain();
  masterGain.gain.setValueAtTime(0, now);
  masterGain.gain.linearRampToValueAtTime(0.7, now + params.fadeInDuration / 1000);
  masterGain.connect(destination);
  nodes.push(masterGain);

  // ---------------------------------------------------------------
  // Layer 1: Minor chord drone (A minor - A, C, E)
  // ---------------------------------------------------------------
  const tensionNotes = [110.00, 130.81, 164.81]; // A2, C3, E3
  
  tensionNotes.forEach((freq, i) => {
    const osc = ctx.createOscillator();
    osc.type = i === 0 ? 'sawtooth' : 'sine';
    osc.frequency.setValueAtTime(freq, now);
    
    // Slow LFO for movement
    const lfo = ctx.createOscillator();
    lfo.type = 'sine';
    lfo.frequency.setValueAtTime(0.1 + i * 0.05, now);
    
    const lfoGain = ctx.createGain();
    lfoGain.gain.setValueAtTime(3, now);
    
    lfo.connect(lfoGain);
    lfoGain.connect(osc.frequency);
    
    // Filter for darkness
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(400, now);
    filter.Q.value = 1;
    
    const oscGain = ctx.createGain();
    oscGain.gain.setValueAtTime(i === 0 ? 0.03 : 0.05, now);
    
    osc.connect(filter);
    filter.connect(oscGain);
    oscGain.connect(masterGain);
    
    osc.start(now);
    lfo.start(now);
    nodes.push(osc, lfo, lfoGain, filter, oscGain);
  });

  // ---------------------------------------------------------------
  // Layer 2: Sub bass pulse
  // ---------------------------------------------------------------
  const subOsc = ctx.createOscillator();
  subOsc.type = 'sine';
  subOsc.frequency.setValueAtTime(55, now); // A1
  
  const subLfo = ctx.createOscillator();
  subLfo.type = 'sine';
  subLfo.frequency.setValueAtTime(0.5, now); // Slow pulse
  
  const subLfoGain = ctx.createGain();
  subLfoGain.gain.setValueAtTime(0.03, now);
  
  const subGain = ctx.createGain();
  subGain.gain.setValueAtTime(0.04, now);
  
  subLfo.connect(subLfoGain);
  subLfoGain.connect(subGain.gain);
  subOsc.connect(subGain);
  subGain.connect(masterGain);
  
  subOsc.start(now);
  subLfo.start(now);
  nodes.push(subOsc, subLfo, subLfoGain, subGain);

  // ---------------------------------------------------------------
  // Return control object
  // ---------------------------------------------------------------
  return {
    stop: () => {
      isPlaying = false;

      const fadeStart = ctx.currentTime;
      masterGain.gain.setValueAtTime(masterGain.gain.value, fadeStart);
      masterGain.gain.linearRampToValueAtTime(0, fadeStart + params.fadeOutDuration / 1000);

      setTimeout(() => {
        nodes.forEach(node => {
          try {
            if (node.stop) node.stop();
            node.disconnect();
          } catch (e) {
            // Node may already be stopped
          }
        });
      }, params.fadeOutDuration + 100);
    }
  };
}

// =====================================================================
// UTILITY: Create pink noise (more pleasant than brown noise)
// =====================================================================

function createPinkNoise(ctx) {
  const bufferSize = ctx.sampleRate * 4; // 4 seconds of noise
  const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
  const data = buffer.getChannelData(0);

  // Pink noise algorithm using Paul Kellet's refined method
  let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
  
  for (let i = 0; i < bufferSize; i++) {
    const white = Math.random() * 2 - 1;
    
    b0 = 0.99886 * b0 + white * 0.0555179;
    b1 = 0.99332 * b1 + white * 0.0750759;
    b2 = 0.96900 * b2 + white * 0.1538520;
    b3 = 0.86650 * b3 + white * 0.3104856;
    b4 = 0.55000 * b4 + white * 0.5329522;
    b5 = -0.7616 * b5 - white * 0.0168980;
    
    data[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362) * 0.11;
    b6 = white * 0.115926;
  }

  const source = ctx.createBufferSource();
  source.buffer = buffer;
  source.loop = true;

  return source;
}

/**
 * Random number between min and max
 */
function randomBetween(min, max) {
  return Math.random() * (max - min) + min;
}
